/**
 * The schema-generated data-set editor — ADR-127 D2, with D3's live validation.
 *
 * D2 makes the generated form the DEFAULT editor and raw JSON the escape hatch, "exactly as
 * SKILL.md and workflow YAML do today". The structural layer is ordinary form rendering; what
 * this component is actually for is the five layer-2 obligations, because "a generated form
 * that ignored layer 2 would be a worse editor than the raw JSON it replaces":
 *
 *   R6  "not yet known" is its own control state — never a blank that looks like a real false
 *   R7  provenance renders as a "confirmed by / on" affordance
 *   R12 the unit is in the label
 *   R14 a deprecated field is struck through, with its date
 *   R18 a resolved field is read-only, with its resolver named
 *
 * TWO SCHEMAS, ON PURPOSE. The form model reads the RAW schema (the `x-` keys ARE layer 2);
 * validation evaluates the COMPILED one (the engines ignore `x-` keys, and compile_schema()
 * materialises the annotations' effects). Passing one where the other belongs produces a
 * plausible editor that drops every obligation above — so the model reports `looksCompiled`
 * and this component refuses to pretend.
 *
 * WHAT IT DOES NOT DO. It does not propose. D3 layer 2 — would this break a consumer — is a
 * separate gate (`POST /api/data-references/impact`) that the propose action must consult;
 * this component surfaces layer 1 as you type and hands the edited document up.
 *
 * WRITES GO THROUGH dataDocumentEdit. Every edit is an immutable, structurally-shared copy
 * addressed by a segment path, because the three ways to lose an operator's row — mutating
 * React's state object, rebuilding a row from only the fields the form models, and moving or
 * deleting by a drifted index — are all avoided there rather than here. A refused edit
 * (past `maxItems`, a stale row index) returns a REASON, which this component shows: a
 * silently-dropped click is how an operator concludes the form is broken and goes to raw.
 */

import { useMemo, useState } from 'react'
import {
  formModelFromSchema, flattenFields, isUnknown,
  type FieldDescriptor, type FormModel,
} from './dataFormModel'
import { validateAgainstCompiled, registryInstance, type SchemaVerdict } from './schemaValidator'
import { FieldDescription, FIELD_LABEL_CLASS } from './FieldCaption'
import {
  getAt, setAt, addRow, removeRow, moveRow, templateRow, rowFieldPath, collectionPath,
  type Segment,
  serializeDocument,
} from './dataDocumentEdit'

export interface DataSetFormProps {
  /** The RAW schema — layer 2 lives in its `x-` keys. */
  schema: unknown
  /** The COMPILED schema, from `validate-reference-data.py --compiled`. */
  compiledSchema: object
  /** The data document being edited. */
  value: unknown
  onChange: (next: unknown) => void
  /** Set for an externally-sourced set (ADR-131 D14): browse only, edit on the integration. */
  /** The filename an export downloads as — the registry path's basename. */
  exportName?: string
  readOnly?: boolean
  readOnlyReason?: string
  /* NO `readOnlyRoute`. A refusal's route is a CONTROL (ADR-168 D10) and the host renders it as
     one, beside this form and on the same screen. This form carried a plain-text second copy of
     the same string, so the operator read the route twice and could click neither copy here. */
}

/** What the text beside a boolean checkbox says. */
function booleanCaption(value: unknown): string {
  if (value === true) return 'True'
  if (value === false) return 'False'
  if (value === undefined) return 'Not set'
  if (value === null) return 'Null'
  // A boolean field holding a string got there through Raw / advanced or an import. The
  // validator's findings already say so at the top; naming it here stops the checkbox from
  // reporting it as a value it is not.
  return 'Invalid value'
}

function FieldRow(props: {
  field: FieldDescriptor
  /** The INSTANCE this control edits — `properties[0].wp_api_base`, not the descriptor's
   *  `properties[].wp_api_base`. Two rows share a descriptor, so the descriptor path cannot
   *  identify a control: it produced duplicate ids, which is ambiguous to a label's `for`, to
   *  anything scripting the page, and to a test. */
  instanceId: string
  value: unknown
  findings: SchemaVerdict['findings']
  disabled: boolean
  onChange: (next: unknown) => void
  /**
   * In a TABLE the column heading already names the field, so repeating the label
   * in every cell is noise that makes 32 records unreadable. The control, its
   * findings and its unknown-sentinel handling are unchanged — only the caption
   * moves to the header.
   */
  labelled?: boolean
}) {
  const { field, instanceId, value, findings, disabled, onChange, labelled = true } = props
  const unknown = isUnknown(field, value)
  const resolved = field.resolvedBy !== undefined
  const locked = disabled || resolved

  return (
    <div data-testid={`field-${instanceId}`}>
      {labelled && (
      <label>
        {/* R14 — struck through, WITH its date. A strike alone says "don't use this" without
            saying since when, which is the half an operator needs to judge urgency. */}
        {/* Bold, and ONE size for every field (George, 2026-08-27). The name and its help
            text used to render at the same weight and scale, so a record read as a wall of
            prose with no way to find where one field ended and the next began. */}
        <span
          data-testid={`label-${instanceId}`}
          className={FIELD_LABEL_CLASS}
          style={field.deprecated ? { textDecoration: 'line-through' } : undefined}
        >
          {field.label}
        </span>
        {field.required && <span aria-hidden> *</span>}
        {field.deprecated && (
          <em data-testid={`deprecated-${instanceId}`}>
            {' '}deprecated{field.deprecated.since ? ` since ${field.deprecated.since}` : ''}
            {field.deprecated.note ? ` — ${field.deprecated.note}` : ''}
          </em>
        )}
      </label>
      )}

      {/* Smaller than the name, and clamped to two lines with a control to open it. A
          description in this corpus is often several paragraphs of ADR prose — six lines of
          it above every input pushed the next field off the screen. */}
      {labelled && (
        <FieldDescription
          text={field.description}
          testId={`help-${instanceId}`}
          label={field.label}
        />
      )}

      {/* R18 — read-only, and the resolver NAMED. "Read-only" alone invites an operator to
          look for the permission they are missing; naming the resolver tells them the value
          is not theirs to set. */}
      {resolved && (
        <p data-testid={`resolved-${instanceId}`}>
          Set by {field.resolvedBy} — editing it here would be overwritten on the next
          resolution.
        </p>
      )}

      {/* R6 — "not yet known" is its OWN state. Rendered as an explicit control rather than
          a blank, because for a boolean a blank is indistinguishable from a real `false`. */}
      {field.unknownSentinel !== undefined && (
        // In a table this wrapped to three lines in EVERY cell of the column, which
        // is most of why a four-record set filled the screen. The state is unchanged
        // and still explicit (R6: a blank must never pass for a real `false`) — it is
        // just spelled at the width a cell has.
        <label
          data-testid={`unknown-${instanceId}`}
          title="Not yet known — distinct from a real value"
          // `mr-4` keeps it off the value control beside it. They are two different
          // questions — "do we know?" and "what is it?" — and with the value now spelled out
          // they ran together as "Not yet known ☑ True".
          className="inline-flex items-center gap-1 mr-4 text-[11px] text-gray-500 whitespace-nowrap"
        >
          <input
            type="checkbox"
            checked={unknown}
            disabled={locked}
            onChange={(e) => onChange(e.target.checked ? field.unknownSentinel : undefined)}
          />
          {labelled ? 'Not yet known' : 'unknown'}
        </label>
      )}

      {!unknown && field.kind === 'enum' && (
        <select
          className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-xs"
          data-testid={`input-${instanceId}`}
          disabled={locked}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">—</option>
          {(field.options ?? []).map((option) => (
            <option key={String(option)} value={String(option)}>{String(option)}</option>
          ))}
        </select>
      )}

      {/* THE VALUE IS SPELLED OUT (George, 2026-08-27). A bare checkbox is the one control
          whose state cannot be read at a glance: an unticked box is `false`, but it looks the
          same as a field nobody has filled in.

          FOUR states, not two. An optional boolean the document simply does not carry is not
          a `false` — `cwv_priority` is set on ONE of spartan's eight website-property records
          and absent on the other seven, so a blanket "False" would assert seven values nobody
          entered. That is R6's confusion one level down, and the same reason a `null` and a
          string that got in through Raw are named rather than flattened into "False".

          `aria-label` carries the FIELD name, because the visible text is the VALUE and the
          control would otherwise announce as "True, checkbox" with nothing saying true of
          what. The caption is NOT hidden from assistive technology: an unchecked box reads
          the same for `false` and for absent, so the caption is the only thing that tells
          them apart, and `aria-describedby` is how it reaches a screen reader. */}
      {!unknown && field.kind === 'boolean' && (
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
          <input
            data-testid={`input-${instanceId}`}
            type="checkbox"
            aria-label={field.label}
            aria-describedby={`boolean-label-${instanceId}`}
            disabled={locked}
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span
            id={`boolean-label-${instanceId}`}
            data-testid={`boolean-label-${instanceId}`}
            className={typeof value === 'boolean' ? undefined : 'text-gray-400 italic'}
          >
            {booleanCaption(value)}
          </span>
        </label>
      )}

      {!unknown && (field.kind === 'string' || field.kind === 'number' || field.kind === 'integer') && (
        <input
          // Fills the cell rather than overflowing it. The cell caps its own width
          // (max-w in the table), so long prose truncates visibly instead of pushing
          // every other column off-screen — and `title` gives the full value on hover
          // without a click.
          className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-xs"
          title={value === undefined || value === null ? undefined : String(value)}
          data-testid={`input-${instanceId}`}
          type={field.kind === 'string' ? 'text' : 'number'}
          disabled={locked}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => {
            const raw = e.target.value
            if (field.kind === 'string') return onChange(raw)
            if (raw === '') return onChange(undefined)
            const parsed = Number(raw)
            // NaN is never sent upward: it would serialise to `null` and read as a deliberate
            // null. An unparseable number stays the operator's problem in the input.
            onChange(Number.isNaN(parsed) ? value : parsed)
          }}
        />
      )}

      {!unknown && field.kind === 'unsupported' && (
        <p data-testid={`unsupported-${instanceId}`}>
          This field's shape is not editable here — use Raw / advanced.
        </p>
      )}

      {findings.length > 0 && (
        <ul data-testid={`errors-${instanceId}`}>
          {findings.map((f, i) => <li key={i}>{f.message}</li>)}
        </ul>
      )}
    </div>
  )
}

export function DataSetForm(props: DataSetFormProps) {
  const { schema, compiledSchema, value, onChange, exportName,
          readOnly, readOnlyReason } = props
  const [raw, setRaw] = useState(false)
  const [rawText, setRawText] = useState(() => JSON.stringify(value, null, 2))
  const [rawError, setRawError] = useState<string | null>(null)

  const model: FormModel = useMemo(() => formModelFromSchema(schema), [schema])
  const [refused, setRefused] = useState<string | null>(null)

  // The keyed collection is the object an operator works with day to day (D1). Its record
  // schema comes from the RAW document, because templateRow reads `required`.
  const collection = useMemo(
    () => model.fields.find((f) => f.kind === 'array' && f.children && f.children.length > 0),
    [model],
  )
  const recordSchema = useMemo(() => {
    if (!collection) return undefined
    const props = (schema as { properties?: Record<string, { items?: unknown }> })?.properties
    return props?.[collection.name]?.items
  }, [schema, collection])
  const rows = collection ? getAt(value, collectionPath(`${collection.name}[]`)) : undefined
  const [importProblem, setImportProblem] = useState<string | null>(null)
  /**
   * THE TABLE IS AN INDEX, NOT THE EDITOR (owner decision 2026-08-13).
   *
   * A record can carry a dozen fields, nested objects and paragraphs of prose. A
   * grid that tries to edit all of that at once is unreadable at any width — every
   * column is too narrow, and nested groups have nowhere to be grouped. So the
   * table answers "which record", and the detail answers "what does it say".
   *
   * This is also where ADR-127 D2's "nested objects as sections" finally has a
   * surface to live on: a section needs vertical room a cell does not have.
   */
  const [openRow, setOpenRow] = useState<number | null>(null)
  const rowList = Array.isArray(rows) ? rows : []

  /**
   * The record's SCALAR LEAVES, in declaration order — one table column each.
   *
   * Objects contribute no column of their own (an object is not a value and a cell
   * cannot hold a subtree); their leaves do, headed by the dotted path. Same rule
   * as the creation grid, so the two surfaces read alike.
   */
  const leafColumns = useMemo(
    () => (collection?.children ?? []).filter(f => f.kind !== 'array' && f.kind !== 'object'),
    [collection])

  /**
   * The index's columns: the key, then the first few other scalars in declaration
   * order. Schema-driven, so nothing is hand-authored per set (ADR-125 D12) — the
   * author already chose the order when they declared the fields, and the first
   * ones are the identifying ones in every set in the corpus.
   */
  const INDEX_COLUMNS = 4
  const summaryColumns = useMemo(() => {
    const key = model.keyProperty
      ? leafColumns.filter(f => f.path.endsWith(`.${model.keyProperty}`))
      : []
    const rest = leafColumns.filter(f => !key.includes(f))
    return [...key, ...rest].slice(0, INDEX_COLUMNS)
  }, [leafColumns, model.keyProperty])
  const hiddenColumnCount = Math.max(0, leafColumns.length - summaryColumns.length)

  /**
   * The detail's layout: the record's own scalars first, then ONE SECTION PER
   * NESTED OBJECT. Recursive, because nesting is (a schema can nest as deep as it
   * likes and everything downstream already recurses); a deeper object's section
   * is titled by its dotted path, so `address.geo` reads as what it is.
   */
  const detailSections = useMemo(() => {
    const sections: { title: string | null; fields: FieldDescriptor[] }[] = []
    const walk = (fields: FieldDescriptor[], title: string | null) => {
      const scalars = fields.filter(f => f.kind !== 'object' && f.kind !== 'array')
      if (scalars.length > 0) sections.push({ title, fields: scalars })
      for (const child of fields.filter(f => f.kind === 'object')) {
        walk(child.children ?? [], child.label)
      }
    }
    walk(collection?.children ?? [], null)
    return sections
  }, [collection])

  const apply = (result: { doc: unknown; refused?: string }) => {
    setRefused(result.refused ?? null)
    if (!result.refused) onChange(result.doc)
  }
  const verdict = useMemo(
    () => validateAgainstCompiled(compiledSchema, registryInstance(value)),
    [compiledSchema, value],
  )

  const findingsFor = (path: string) => {
    // Ajv reports an INSTANCE pointer; a descriptor carries a SCHEMA path. Match on the
    // trailing property name, which is what a field-level error list needs — and deliberately
    // approximate: a wrong-but-adjacent highlight is better than dropping the error, and the
    // full list is always shown at the top.
    const name = path.split('.').pop()?.replace('[]', '') ?? ''
    return verdict.findings.filter((f) => f.pointer.endsWith(`/${name}`))
  }

  if (model.looksCompiled) {
    // Refuse rather than render. A form built from the compiled schema looks completely
    // normal and silently drops every layer-2 obligation — units, deprecations, resolvers,
    // the unknown state. Better to say so than to be quietly wrong.
    return (
      <div data-testid="form-wrong-schema" role="alert">
        <p>
          This form was given a COMPILED schema. Layer-2 semantics (units, deprecations,
          resolvers, "not yet known") live in the raw schema's <code>x-</code> keys, which the
          compiler strips — so the editor would silently drop all of them. Pass the raw schema.
        </p>
      </div>
    )
  }

  return (
    <div data-testid="data-set-form">
      {/* WHY the grid is browse-only, and only that. The route out of a refusal is the host's
          (ADR-168 D10): it is a control there, and a set editable at the values layer but denied
          at the contract layer still gets it — from the host, which knows about both layers. */}
      {readOnly && (
        <div data-testid="form-read-only" role="status">
          <p>{readOnlyReason ?? 'This set is not editable here.'}</p>
        </div>
      )}

      {model.effectiveDated && (
        // D2 / ADR-125 D13: the verb is "schedule", not "edit". Said at the top, because the
        // operator's mental model has to change before they touch a field.
        <p data-testid="form-timeline-notice">
          This set is effective-dated: changes are <strong>scheduled</strong> from a date, and
          past records are corrected by publishing a new version rather than edited.
        </p>
      )}

      {model.provenance.length > 0 && (
        // R7 — the "confirmed by / on" affordance, stated where it applies.
        <p data-testid="form-provenance">
          Each record records who confirmed it and when
          {': '}
          {model.provenance.map((p) => p.fields.join(', ')).join('; ')}.
        </p>
      )}

      {/* A TOOLBAR, not a run of adjacent text. These read as "Raw / advancedExport
          JSONImport JSON Browse... No file selected" because nothing was styled and
          the native file input was exposed raw. */}
      <div className="flex items-center gap-2 my-3">
      <button
        data-testid="form-raw-toggle"
        onClick={() => setRaw(!raw)}
        className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
      >
        {raw ? 'Form' : 'Raw / advanced'}
      </button>

      {/* EXPORT / IMPORT — the local-editing round trip.
          A set is a JSON document, and sometimes the right tool is the operator's
          own editor: a bulk find-and-replace, a script, a diff against last
          quarter's list. Export hands them exactly the bytes the registry holds;
          import takes them back. */}
      <button
        data-testid="form-export"
        className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
        onClick={() => {
          const blob = new Blob([serializeDocument(value)], { type: 'application/json' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = exportName ?? 'data.json'
          a.click()
          URL.revokeObjectURL(url)
        }}
      >
        Export JSON
      </button>

      {!readOnly && (
        <label
          data-testid="form-import-label"
          className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
        >
          Import JSON
          <input
            className="sr-only"
            type="file"
            accept="application/json,.json"
            data-testid="form-import"
            onChange={async (e) => {
              const file = e.target.files?.[0]
              e.target.value = ''            // so re-picking the same file fires again
              if (!file) return
              setImportProblem(null)
              let parsed: unknown
              try {
                parsed = JSON.parse(await file.text())
              } catch (err) {
                setImportProblem(
                  `That file is not valid JSON: ${err instanceof Error ? err.message : String(err)}`)
                return
              }
              // VALIDATE BEFORE ACCEPTING. An uploaded file is the one input nobody
              // has typed under live validation, so it is the likeliest to be wrong
              // — and replacing the document first and reporting afterwards would
              // destroy what the operator had. The findings render in the same list
              // as every other finding; the canonical validator remains the
              // authority on the propose path (ADR-129 D7).
              const check = validateAgainstCompiled(compiledSchema, registryInstance(parsed))
              if (!check.valid) {
                setImportProblem(
                  `That file does not satisfy ${'this set'}'s schema, so it was NOT loaded:\n`
                  + check.findings.map(f => `${f.pointer || '(document)'}: ${f.message}`).join('\n'))
                return
              }
              onChange(parsed)
            }}
          />
        </label>
      )}

      </div>

      {importProblem && (
        <pre
          data-testid="form-import-problem"
          role="alert"
          className="text-xs text-red-700 whitespace-pre-wrap bg-red-50 border border-red-200 rounded p-2 mb-3"
        >{importProblem}</pre>
      )}

      {!verdict.valid && (
        <ul data-testid="form-findings">
          {verdict.findings.map((f, i) => (
            <li key={i}>{f.pointer || '(document)'}: {f.message}</li>
          ))}
        </ul>
      )}

      {raw ? (
        <div>
          <textarea
            data-testid="form-raw"
            value={rawText}
            onChange={(e) => {
              setRawText(e.target.value)
              try {
                onChange(JSON.parse(e.target.value))
                setRawError(null)
              } catch (err) {
                // Keep the operator's text; do NOT propagate half-typed JSON upward, or a
                // keystroke mid-object would look like a deliberate deletion.
                setRawError(err instanceof Error ? err.message : String(err))
              }
            }}
          />
          {rawError && <p data-testid="form-raw-error">{rawError}</p>}
        </div>
      ) : (
        <div>
          {refused && <p data-testid="form-refused" role="alert">{refused}</p>}

          {/* Fields outside the collection. */}
          {flattenFields(model.fields)
            .filter((f) => f.kind !== 'array' && f.kind !== 'object' && !f.path.includes('[]'))
            .map((f) => (
              <FieldRow
                key={f.path}
                field={f}
                instanceId={f.path}
                value={getAt(value, f.path.split('.') as Segment[])}
                findings={findingsFor(f.path)}
                disabled={!!readOnly}
                onChange={(next) =>
                  apply({ doc: setAt(value, f.path.split('.') as Segment[], next) })}
              />
            ))}

          {collection && (
            <div data-testid="form-rows">
              {/* A TABLE, not a form per record.
                  The stacked-fieldset version rendered one record at a time — "1 of 32
                  records" — so reading a set meant scrolling 32 forms and comparing values
                  from memory. Reference data is tabular by nature (R1: a keyed collection of
                  HOMOGENEOUS records), and the point of looking at it is comparison.
                  Columns are the record's SCALAR LEAVES, headed by the dotted path
                  (`address.city`), which is both the spelling a consumer uses and the same
                  design the creation grid uses — one mental model for both surfaces. */}
              {/* FULL-WIDTH DETAIL (owner decision 2026-08-16): the index steps aside
                  while a record is open, rather than sharing the width with it. Deep
                  nesting and paragraph fields need the room more than the list does.
                  What that costs is list context — you can no longer see where you are
                  — so the detail carries prev/next stepping to give it back. */}
              {openRow === null && (
              <div className="overflow-x-auto border border-gray-200 rounded">
              <table
                data-testid="form-rows-table"
                className="min-w-full text-sm border-collapse"
              >
                {/* Sticky header, and a sticky KEY column. Scrolled right (and this
                    table WILL scroll right — a record can have a dozen fields) the
                    previous version lost both: you could not tell which column you
                    were in or which record you were on. */}
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="sticky left-0 z-20 bg-gray-50 text-left text-xs font-medium text-gray-600 px-3 py-2 border-b border-r border-gray-200 whitespace-nowrap"
                    >{model.keyProperty
                        ? (leafColumns.find(f => f.path.endsWith(`.${model.keyProperty}`))?.label
                            ?? model.keyProperty)
                        : '#'}</th>
                    {summaryColumns.map((f) => (
                      <th
                        key={f.path}
                        scope="col"
                        data-testid={`col-${f.path}`}
                        className="text-left text-xs font-medium text-gray-600 px-3 py-2 border-b border-gray-200 whitespace-nowrap"
                      >
                        {f.label}
                        {f.required && <span aria-hidden> *</span>}
                        {model.keyProperty && f.path.endsWith(`.${model.keyProperty}`) && ' (key)'}
                      </th>
                    ))}
                    <th
                      scope="col"
                      className="text-left text-xs font-medium text-gray-600 px-3 py-2 border-b border-gray-200 whitespace-nowrap"
                    >Detail</th>
                    <th
                      scope="col"
                      className="sticky right-0 z-20 bg-gray-50 text-left text-xs font-medium text-gray-600 px-3 py-2 border-b border-l border-gray-200 whitespace-nowrap"
                    >Row</th>
                  </tr>
                </thead>
                <tbody>
                  {rowList.map((_row, index) => (
                    <tr
                      key={index}
                      data-testid={`row-${index}`}
                      className="odd:bg-white even:bg-gray-50/50 hover:bg-indigo-50/40 align-top"
                    >
                      <td className="sticky left-0 z-10 bg-inherit px-3 py-1.5 border-b border-r border-gray-200 text-xs font-mono text-gray-700 whitespace-nowrap">
                        {model.keyProperty
                          ? String((rowList[index] as Record<string, unknown>)?.[model.keyProperty]
                              ?? index + 1)
                          : index + 1}
                      </td>
                      {/* READ-ONLY summaries. The index identifies a record; editing
                          happens in the detail, where a field has room for its label,
                          its help text and — for a nested object — its own section. */}
                      {summaryColumns.map((f) => {
                        const cell = getAt(value, rowFieldPath(f.path, index))
                        const text = cell === undefined || cell === null || cell === ''
                          ? '—'
                          : typeof cell === 'boolean' ? (cell ? 'yes' : 'no') : String(cell)
                        return (
                          <td
                            key={f.path}
                            data-testid={`cell-${f.path.replace('[]', `[${index}]`)}`}
                            title={text}
                            className="px-3 py-1.5 border-b border-gray-200 max-w-[18rem] truncate text-gray-700"
                          >
                            {text}
                          </td>
                        )
                      })}
                      <td className="px-3 py-1.5 border-b border-gray-200 whitespace-nowrap">
                        <button
                          data-testid={`row-open-${index}`}
                          onClick={() => setOpenRow(index)}
                          className="px-2 py-0.5 text-xs rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        >
                          Open{hiddenColumnCount > 0 ? ` (+${hiddenColumnCount} more)` : ''}
                        </button>
                      </td>
                      {/* One line of compact buttons, sticky right. These were three
                          stacked plain-text links — they neither looked like actions
                          nor fitted on one line, so every row was three lines tall. */}
                      <td className="sticky right-0 z-10 bg-inherit px-2 py-1.5 border-b border-l border-gray-200 whitespace-nowrap">
                        <span className="inline-flex gap-1">
                        <button
                          title="Remove this record"
                          className="px-1.5 py-0.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300 disabled:opacity-30"
                          data-testid={`row-remove-${index}`}
                          disabled={!!readOnly}
                          onClick={() => apply(removeRow(value, collectionPath(collection.path + '[]'), index))}
                        >
                          Remove
                        </button>
                        <button
                          title="Move up"
                          className="px-1.5 py-0.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-30"
                          data-testid={`row-up-${index}`}
                          disabled={!!readOnly || index === 0}
                          onClick={() => apply(moveRow(value, collectionPath(collection.path + '[]'), index, index - 1))}
                        >
                          ↑
                        </button>
                        <button
                          title="Move down"
                          className="px-1.5 py-0.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-30"
                          data-testid={`row-down-${index}`}
                          disabled={!!readOnly || index === rowList.length - 1}
                          onClick={() => apply(moveRow(value, collectionPath(collection.path + '[]'), index, index + 1))}
                        >
                          ↓
                        </button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              )}
              {/* THE ROW DETAIL — the editor.
                  Every field of one record, at full width, with its label, its help
                  text and its findings. A nested object becomes a SECTION, which is
                  what ADR-127 D2 asked for and what a grid cell could never give it. */}
              {openRow !== null && rowList[openRow] !== undefined && (
                <div
                  data-testid="row-detail"
                  className="border border-gray-200 rounded bg-white p-4"
                >
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-indigo-200">
                    <div className="flex items-center gap-3 min-w-0">
                      <button
                        data-testid="row-detail-close"
                        onClick={() => setOpenRow(null)}
                        className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 whitespace-nowrap"
                      >
                        ← All records
                      </button>
                      <h4
                        className="text-sm font-semibold text-gray-900 truncate"
                        data-testid="row-detail-title"
                      >
                        {model.keyProperty
                          ? String((rowList[openRow] as Record<string, unknown>)?.[model.keyProperty]
                              ?? `Record ${openRow + 1}`)
                          : `Record ${openRow + 1}`}
                      </h4>
                    </div>
                    {/* Stepping, because full width hides the list: correcting a field
                        across every record is the common job, and going back to the
                        index between each one is the tax this removes. */}
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-gray-500" data-testid="row-detail-position">
                        {openRow + 1} of {rowList.length}
                      </span>
                      <button
                        data-testid="row-detail-prev"
                        disabled={openRow === 0}
                        onClick={() => setOpenRow(openRow - 1)}
                        title="Previous record"
                        className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-30"
                      >↑</button>
                      <button
                        data-testid="row-detail-next"
                        disabled={openRow >= rowList.length - 1}
                        onClick={() => setOpenRow(openRow + 1)}
                        title="Next record"
                        className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-30"
                      >↓</button>
                    </div>
                  </div>
                  {detailSections.map((section) => (
                    <div key={section.title ?? '__root'} className="mb-3">
                      {section.title && (
                        // The section heading IS the object. Without it, `city` and
                        // `state` are just two more fields and nothing says they
                        // belong to `address`.
                        <h5
                          data-testid={`detail-section-${section.title}`}
                          className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1"
                        >
                          {section.title}
                        </h5>
                      )}
                      <div className="space-y-2">
                        {section.fields.map((f) => (
                          <FieldRow
                            key={f.path}
                            field={f}
                            instanceId={f.path.replace('[]', `[${openRow}]`)}
                            value={getAt(value, rowFieldPath(f.path, openRow))}
                            findings={findingsFor(f.path)}
                            disabled={!!readOnly}
                            onChange={(next) =>
                              apply({ doc: setAt(value, rowFieldPath(f.path, openRow), next) })}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                data-testid="row-add"
                className="mt-2 px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
                disabled={!!readOnly}
                onClick={() => apply(addRow(value, collectionPath(collection.path + '[]'),
                  templateRow(recordSchema), collection.maxItems))}
              >
                Add record
              </button>
              {typeof collection.maxItems === 'number' && (
                // Shown BEFORE the bound is hit, not as an error after — the operator plans
                // around a ceiling they can see.
                <p data-testid="row-bound">
                  {rowList.length} of {collection.maxItems} records
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
